import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material'
import Accordion from '@mui/material/Accordion'
import { TaxiVehicleType } from 'api/models/Common/JyrneyEnums'
import { TaxiRate } from 'api/vehicle/taxisRatesAPI.types'
import SectionHeader from 'components/Common/SectionHeader'
import Policies from 'components/FormFields/PolicyFields/PolicyFields'
import Form from 'components/RouteWizard/Form'
import Input from 'components/RouteWizard/Input'
import { Select } from 'components/RouteWizard/Select'
import React, { useEffect, useRef } from 'react'
import { Controller, UseFormReturn } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import {
  createClientTaxiRates,
  getClientTaxiRates,
  getClientTaxiRatesById,
  updateClientTaxiRatesById,
} from 'reducers/clientsReducer'
import PickupAndWait from './PickupAndWait/PickupAndWait'
import RoutePricing from './RoutePricing/RoutePricing'
import { TaxiRateAreas } from './TaxiRateAreas/TaxiRateArea'
import taxiRateSchema from './taxiRateSchema'
import WaitAndReturnClient from './WaitAndReturn/WaitAndReturn'

type ManageTaxiRatesProps = {
  client: any
  rateId?: number
  view?: boolean
  closePage: () => void
}

const pageHeader = 'Manage Mileage Rates'
const pageInfo = 'Create / Edit your Mileage Rates.'

const ManageTaxiRates = ({
  client,
  rateId,
  closePage,
}: ManageTaxiRatesProps) => {
  console.log('Manage Taxi Rates: client: ', client)
  const dispatch = useDispatch()
  const [editRate, setEditRate] = React.useState(false)
  const [rateDetails, setRateDetails] = React.useState<TaxiRate | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [chargeRates, setChargeRates] = React.useState(
    rateDetails?.chargeRates || [],
  )
  const [waitAndReturn, setWaitAndReturn] = React.useState(false)
  const [additionalStops, setAdditionalStops] = React.useState(false)
  const [waitingTime, setWaitingTime] = React.useState<string>('')

  console.log('chargeRates: ', chargeRates)

  const formMethods = useRef<UseFormReturn | null>(null)

  useEffect(() => {
    if (rateId !== undefined) {
      setIsLoading(true)
      dispatch<any>(
        getClientTaxiRatesById({
          clientId: client.id as number,
          taxiRateId: rateId as number,
        }),
      )
        .then((response: { payload: TaxiRate }) => {
          setRateDetails(response.payload)
          setChargeRates(response.payload.chargeRates || [])
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [rateId, client.clientId, dispatch])

  useEffect(() => {
    formMethods.current?.reset({ waitingTime: '' })
  }, [waitingTime, setWaitingTime])

  useEffect(() => {
    formMethods.current?.reset({ waitAndReturn: '' })
  }, [waitAndReturn, setWaitAndReturn])

  const onSubmit = async (data: any) => {
    console.log('Form submitted with data:', data)
    const submitData = {
      ...data,
    }
    setEditRate(false)
    if (!editRate) {
      try {
        dispatch(
          createClientTaxiRates({
            // data: dirtyValues as TaxiRate,
            data: submitData,
            clientId: client.clientId,
          }),
        )
      } catch (error) {
        console.error('Error creating rate:', error)
      }
    } else {
      try {
        await dispatch(
          updateClientTaxiRatesById({
            clientId: client.clientId,
            taxiRateId: rateId!,
            data: submitData,
          }),
        )
        closePage()
      } catch (error) {
        console.error('Error updating rate:', error)
      }
      console.log('Updating rate with data:', { ...submitData })
    }
    getClientTaxiRates(client.clientId)
  }

  useEffect(() => {
    setIsLoading(!!rateId && !rateDetails)
    if (rateDetails?.additionalStop != null) {
      setAdditionalStops(true)
    }
  }, [rateId, rateDetails])

  const defaultValues = {
    vehicleTypeName: '',
    minimumFare: { price: 0 },
  }

  console.log('values: ', rateId === undefined, editRate)

  return (
    <Box component={'div'} sx={{ maxWidth: '95%' }}>
      <Grid container spacing={2} sx={{ minWidth: '85%' }}>
        <Grid
          item
          xs={12}
          sx={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <SectionHeader
            title={`${pageHeader} for ${client.companyName}`}
            subtitle={pageInfo}
          />
          <Grid item>
            <Grid container spacing={0} alignItems="center">
              {!editRate && (
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => closePage()}
                  >
                    Back to rates
                  </Button>
                </Grid>
              )}
              {editRate && (
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setEditRate(false)}
                  >
                    Cancel
                  </Button>
                </Grid>
              )}
              {!editRate && rateId !== undefined && (
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setEditRate(true)}
                  >
                    Edit Rate
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>

        {isLoading ? (
          <Grid item xs={12} sx={{ marginTop: '20px' }}>
            <p>Loading...</p>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Form
              isLoading={isLoading}
              schema={taxiRateSchema}
              defaultValues={rateDetails || defaultValues}
              ref={formMethods}
              onSubmit={onSubmit}
              showControls={false}
              sx={{ width: '100%' }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} alignItems="center">
                  <Grid item xs={4} alignItems="center">
                    <Input
                      name="areaGroupName"
                      control={formMethods.current?.control}
                      label="Area Group Name"
                      type="text"
                      disabled={!!rateId && !editRate}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={4} sx={{ paddingTop: 2, paddingBottom: 2 }}>
                    <Select
                      name="vehicleTypeName"
                      options={TaxiVehicleType}
                      label={'Vehicle Type'}
                      topRow={{ value: '', label: 'Select Vehicle Type' }}
                      control={formMethods.current?.control}
                      disabled={!!rateId && !editRate}
                      searchable
                      sx={{ width: '100%' }}
                      {...{ readOnly: !!rateId && !editRate }}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ paddingBottom: 2 }}>
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                      >
                        <Typography component="span">
                          <SectionHeader
                            title="Additional Stop Charges"
                            subtitle={
                              'Do you want to charge additional charges when the client books additional stops?'
                            }
                          />
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={additionalStops}
                              onChange={() =>
                                setAdditionalStops(!additionalStops)
                              }
                              disabled={!!rateId && !editRate}
                              color="primary"
                            />
                          }
                          labelPlacement="start"
                          label={'Apply Charges'}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                  <Grid item xs={12} sx={{ paddingBottom: 2 }}>
                    <Accordion defaultExpanded>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                      >
                        <Typography component="span">
                          <SectionHeader
                            title="Rate Pricing"
                            subtitle={
                              'Specify your rates per mile and any additional charges'
                            }
                          />
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <RoutePricing
                          waitingTime={waitingTime}
                          chargeRates={chargeRates}
                          isEdit={editRate || rateId === undefined}
                          additionalStop={additionalStops}
                          waitAndReturn={waitAndReturn}
                          setChargeRates={setChargeRates}
                          rateDetail={
                            rateDetails || {
                              vehicleTypeName: '',
                              minimumFare: { price: 0 },
                              chargeRates: [],
                            }
                          } // Provide a default value
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                  <Grid item xs={12} sx={{ paddingBottom: 2 }}>
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                      >
                        <Typography component="span">
                          <SectionHeader
                            title={'Area Setting'}
                            subtitle={
                              'Which Postcodes does this rate apply to?'
                            }
                          />
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Controller
                          name="areas"
                          control={formMethods.current?.control}
                          defaultValue={rateDetails?.areas || []}
                          render={({ field }) => (
                            <TaxiRateAreas
                              areas={field.value}
                              onChange={field.onChange}
                              isEdit={editRate || rateId === undefined}
                            />
                          )}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Grid>

                  <Grid item xs={12}>
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                      >
                        <Typography component="span">
                          <SectionHeader
                            title="Pickup Waiting Charges"
                            subtitle={
                              'The ability to specify the free period or grace period charges for this rate'
                            }
                          />
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <PickupAndWait
                          control={formMethods.current?.control}
                          waitingTime={rateDetails?.waitingTime}
                          onChange={(value: string) => {
                            setWaitingTime(value)
                          }}
                          isEdit={editRate || rateId === undefined}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                  <Grid item xs={12} sx={{ paddingTop: 2 }}>
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                      >
                        <Typography component="span">
                          <SectionHeader
                            title="Wait and Return Charges"
                            subtitle={
                              'Do you charge for wait and return journeys?  If so, specify the time in minutes.  The charge will be specified in the Rate Pricing'
                            }
                          />
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <WaitAndReturnClient
                          control={formMethods.current?.control}
                          waitAndReturn={rateDetails?.waitAndReturn}
                          onChange={(value: string) => {
                            setWaitAndReturn(value)
                          }}
                          isEdit={editRate || rateId === undefined}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </Grid>
                <Grid item xs={12} sx={{ paddingTop: 2, paddingBottom: 2 }}>
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="policy-manager"
                      id="policy-manager"
                    >
                      <Typography component="span">
                        <SectionHeader
                          title="Policies"
                          subtitle={
                            'Set the policies appliable to the journeys within the platform'
                          }
                        />
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Policies
                        client={client}
                        rate={rateId}
                        policies={{
                          cancellationPolicies:
                            rateDetails?.cancellationPolicies ?? [],
                          noShowPolicies: rateDetails?.noShowPolicies ?? [],
                          amendPolicies: rateDetails?.amendPolicies ?? [],
                        }}
                        updateFn={updateClientTaxiRatesById}
                        updateSchema={{
                          clientId: client.id,
                          taxiRateId: rateId,
                        }}
                        editPolicy={editRate}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Grid>
                <Grid
                  item
                  xs={12}
                  container
                  justifyContent="flex-end"
                  //   spacing={0}
                >
                  {(rateId === undefined || editRate) && (
                    <Grid item>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isLoading}
                      >
                        {editRate ? 'Update Rate' : 'Create Rate'}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Form>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}

export default ManageTaxiRates
